import { useCallback, useEffect, useRef, useState } from 'react';
import { Database } from 'lucide-react';
import { DEFAULT_THEME_ID, applyTheme, normalizeThemeId } from './config/themes';
import { TopBar } from './components/layout/TopBar';
import { Footer } from './components/layout/Footer';
import { MobileNav } from './components/layout/MobileNav';
import { Sidebar } from './components/layout/Sidebar';
import { PwaNotices } from './components/layout/PwaNotices';
import { getNavigationItem, type ViewId } from './components/layout/navigation';
import { CategoryDialog } from './components/modals/CategoryDialog';
import { EntryDialog } from './components/modals/EntryDialog';
import { QuickAddDialog } from './components/modals/QuickAddDialog';
import { SearchDialog } from './components/modals/SearchDialog';
import { RecordsView } from './components/modules/RecordsView';
import { CalendarView, type CalendarItem } from './pages/CalendarView';
import { toDateKey } from './pages/calendar-utils';
import { FavoritesView } from './pages/FavoritesView';
import { HomeView } from './pages/HomeView';
import { ModuleView } from './pages/ModuleView';
import { MoreView } from './pages/MoreView';
import { SettingsView } from './pages/SettingsView';
import { BackupsView } from './pages/BackupsView';
import { deleteCategoryAndDetach, deleteRecord, explainStorageError, loadPlannerData, replacePlannerStateWithRollback, saveCategory, saveRecord, saveTaskOccurrence, subscribeToDatabaseChanges } from './services/db';
import type { BackupPayload, Category, ContentStore, Note, PlannerData, PlannerRecord, RecordKind, Settings, ShoppingItem, Task, TaskOccurrence } from './types';
import { createLocalId } from './types';
import { APP_VERSION, registerPwa, type PwaUpdate } from './services/pwa';

const SETTINGS_KEY = 'spookyPlans.settings.v4';
const EMPTY_DATA: PlannerData = { tasks: [], taskOccurrences: [], events: [], notes: [], shoppingItems: [], categories: [] };
const DEFAULT_SETTINGS: Settings = { schemaVersion: 4, themeId: DEFAULT_THEME_ID, weekStartsOn: 0, timeFormat: 12 };

const readSettings = (): Settings => {
  try {
    const stored = JSON.parse(localStorage.getItem(SETTINGS_KEY) ?? 'null') as Partial<Settings> & { version?: unknown } | null;
    const themeId = normalizeThemeId(stored?.themeId) ?? DEFAULT_THEME_ID;
    return {
      schemaVersion: 4,
      themeId,
      weekStartsOn: stored?.weekStartsOn === 1 ? 1 : 0,
      timeFormat: stored?.timeFormat === 24 ? 24 : 12
    };
  } catch {
    return DEFAULT_SETTINGS;
  }
};

const storeForKind: Record<RecordKind, ContentStore> = { task: 'tasks', event: 'events', note: 'notes', shopping: 'shoppingItems' };
const viewForKind: Record<RecordKind, ViewId> = { task: 'tasks', event: 'events', note: 'notes', shopping: 'shopping' };

export const App = () => {
  const [settings, setSettings] = useState<Settings>(readSettings);
  const [activeView, setActiveView] = useState<ViewId>('home');
  const [data, setData] = useState<PlannerData>(EMPTY_DATA);
  const [dataState, setDataState] = useState<'loading' | 'ready' | 'error'>('loading');
  const [message, setMessage] = useState('');
  const [quickAddOpen, setQuickAddOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [entryState, setEntryState] = useState<{ kind: RecordKind; record: PlannerRecord | null } | null>(null);
  const [categoryState, setCategoryState] = useState<Category | null | undefined>(undefined);
  const [offline, setOffline] = useState(() => !navigator.onLine);
  const [pwaUpdate, setPwaUpdate] = useState<PwaUpdate | null>(null);
  const mainRef = useRef<HTMLElement>(null);
  const shownReminders = useRef(new Set<string>());
  const activeNavigation = getNavigationItem(activeView);

  const refreshData = useCallback(async () => {
    try {
      const nextData = await loadPlannerData();
      setData(nextData);
      setDataState('ready');
    } catch (error) {
      setDataState('error');
      setMessage(explainStorageError(error));
    }
  }, []);

  useEffect(() => {
    void refreshData();
    const unsubscribe = subscribeToDatabaseChanges(() => void refreshData());
    const blocked = () => setMessage('Another Spooky Plan tab is blocking a database update. Close the other tab and return here.');
    const versionChanged = () => setMessage('The planner database changed in another tab. Reload this page before editing.');
    window.addEventListener('spooky-plans-blocked', blocked);
    window.addEventListener('spooky-plans-versionchange', versionChanged);
    return () => {
      unsubscribe();
      window.removeEventListener('spooky-plans-blocked', blocked);
      window.removeEventListener('spooky-plans-versionchange', versionChanged);
    };
  }, [refreshData]);

  useEffect(() => {
    let disposed = false;
    let unregisterListeners: () => void = () => undefined;
    const updateConnection = () => setOffline(!navigator.onLine);
    window.addEventListener('online', updateConnection);
    window.addEventListener('offline', updateConnection);
    void registerPwa(setPwaUpdate, setMessage)
      .then((cleanup) => {
        if (disposed) cleanup();
        else unregisterListeners = cleanup;
      })
      .catch((error) => setMessage(error instanceof Error ? `Offline setup could not finish: ${error.message}` : 'Offline setup could not finish in this browser.'));
    return () => {
      disposed = true;
      unregisterListeners();
      window.removeEventListener('online', updateConnection);
      window.removeEventListener('offline', updateConnection);
    };
  }, []);

  useEffect(() => {
    applyTheme(settings.themeId);
    try {
      localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
    } catch (error) {
      setMessage(explainStorageError(error));
    }
  }, [settings]);

  useEffect(() => {
    if (dataState !== 'ready') return;
    const checkReminders = () => {
      const now = new Date();
      const today = toDateKey(now);
      const time = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
      const due = [
        ...data.tasks.filter((task) => task.dueDate === today && task.reminder === time).map((task) => ({ key: `task-${task.id}-${today}-${time}`, title: task.title })),
        ...data.events.filter((event) => event.date === today && event.reminder === time).map((event) => ({ key: `event-${event.id}-${today}-${time}`, title: event.title }))
      ].find((item) => !shownReminders.current.has(item.key));
      if (due) {
        shownReminders.current.add(due.key);
        setMessage(`Reminder: ${due.title}`);
      }
    };
    checkReminders();
    const timer = window.setInterval(checkReminders, 30_000);
    return () => window.clearInterval(timer);
  }, [data, dataState]);

  const navigate = (viewId: ViewId) => {
    setActiveView(viewId);
    window.scrollTo({ top: 0 });
    window.requestAnimationFrame(() => mainRef.current?.focus());
  };

  const openNew = (kind: RecordKind) => setEntryState({ kind, record: null });
  const openEdit = (kind: RecordKind, record: PlannerRecord) => setEntryState({ kind, record });

  const saveEntry = async (kind: RecordKind, record: PlannerRecord) => {
    try {
      await saveRecord(storeForKind[kind], record);
      await refreshData();
    } catch (error) {
      throw new Error(explainStorageError(error));
    }
  };

  const removeEntry = async (kind: RecordKind, record: PlannerRecord) => {
    const title = 'name' in record ? record.name : record.title;
    if (!window.confirm(`Delete “${title}”? This cannot be undone.`)) return;
    try {
      await deleteRecord(storeForKind[kind], record.id);
      await refreshData();
      setMessage('Item deleted from this device.');
    } catch (error) {
      setMessage(explainStorageError(error));
    }
  };

  const patchRecord = async (kind: RecordKind, record: PlannerRecord, patch: Partial<PlannerRecord>) => {
    try {
      await saveRecord(storeForKind[kind], { ...record, ...patch, updatedAt: new Date().toISOString() } as PlannerRecord);
      await refreshData();
    } catch (error) {
      setMessage(explainStorageError(error));
    }
  };

  const isCompleted = (kind: RecordKind, record: PlannerRecord) => {
    if (kind === 'shopping') return (record as ShoppingItem).completed;
    if (kind !== 'task') return false;
    const task = record as Task;
    if (task.recurrence === 'none') return task.status === 'completed';
    const today = toDateKey(new Date());
    return data.taskOccurrences.some((occurrence) => occurrence.taskId === task.id && occurrence.occurrenceDate === today && occurrence.completed);
  };

  const toggleComplete = async (kind: RecordKind, record: PlannerRecord) => {
    if (kind === 'shopping') return patchRecord(kind, record, { completed: !(record as ShoppingItem).completed } as Partial<PlannerRecord>);
    if (kind !== 'task') return;
    const task = record as Task;
    const now = new Date().toISOString();
    if (task.recurrence !== 'none') {
      const occurrenceDate = toDateKey(new Date());
      const existing = data.taskOccurrences.find((occurrence) => occurrence.taskId === task.id && occurrence.occurrenceDate === occurrenceDate);
      const completed = !existing?.completed;
      const occurrence: TaskOccurrence = { id: existing?.id ?? createLocalId('occurrence'), taskId: task.id, occurrenceDate, completed, completedAt: completed ? now : null, updatedAt: now };
      try {
        await saveTaskOccurrence(occurrence);
        await refreshData();
      } catch (error) {
        setMessage(explainStorageError(error));
      }
      return;
    }
    const completed = task.status !== 'completed';
    await patchRecord(kind, record, { status: completed ? 'completed' : 'pending', completedAt: completed ? now : null } as Partial<PlannerRecord>);
  };

  const toggleChecklist = async (kind: RecordKind, record: PlannerRecord, itemId: string) => {
    if (!('checklist' in record)) return;
    const checklist = record.checklist.map((item) => item.id === itemId ? { ...item, completed: !item.completed } : item);
    await patchRecord(kind, record, { checklist } as Partial<PlannerRecord>);
  };

  const saveCategoryEntry = async (category: Category) => {
    try {
      await saveCategory(category);
      await refreshData();
      setMessage('Category saved on this device.');
    } catch (error) {
      throw new Error(explainStorageError(error));
    }
  };

  const removeCategory = async (category: Category) => {
    if (category.kind === 'default') return;
    if (!window.confirm(`Delete “${category.name}”? Planner items will remain and become uncategorized.`)) return;
    try {
      await deleteCategoryAndDetach(category.id);
      await refreshData();
      setMessage('Category deleted. Related planner items were kept without a category.');
    } catch (error) {
      setMessage(explainStorageError(error));
    }
  };

  const commonRecordActions = {
    onDelete: removeEntry,
    onEdit: openEdit,
    onToggleChecklist: toggleChecklist,
    onToggleComplete: toggleComplete,
    onToggleFavorite: (kind: RecordKind, record: PlannerRecord) => patchRecord(kind, record, { favorite: !record.favorite }),
    onTogglePinned: (record: PlannerRecord) => patchRecord('note', record, { pinned: !(record as Note).pinned } as Partial<PlannerRecord>)
  };

  const recordsView = (kind: RecordKind, records: PlannerRecord[]) => <RecordsView
    categories={data.categories}
    isCompleted={(record) => isCompleted(kind, record)}
    kind={kind}
    onDelete={(record) => removeEntry(kind, record)}
    onEdit={(record) => openEdit(kind, record)}
    onNew={() => openNew(kind)}
    onToggleChecklist={(record, itemId) => toggleChecklist(kind, record, itemId)}
    onToggleComplete={(record) => void toggleComplete(kind, record)}
    onToggleFavorite={(record) => void patchRecord(kind, record, { favorite: !record.favorite })}
    onTogglePinned={(record) => void commonRecordActions.onTogglePinned(record)}
    records={records}
    timeFormat={settings.timeFormat}
  />;

  const calendarItems: CalendarItem[] = [
    ...data.tasks.filter((task) => task.dueDate).map((task) => ({ id: task.id, kind: 'task' as const, title: task.title, date: task.dueDate!, time: task.dueTime })),
    ...data.events.map((event) => ({ id: event.id, kind: 'event' as const, title: event.title, date: event.date, time: event.startTime }))
  ];

  const restorePlanner = async (payload: BackupPayload) => {
    const previousData = data;
    const previousSettings = settings;
    await replacePlannerStateWithRollback(payload, previousData, previousSettings);
    setSettings(payload.settings);
    await refreshData();
  };

  const renderActiveView = () => {
    if (dataState === 'loading') return <section className="state-card" aria-live="polite">Opening your local planner...</section>;
    if (dataState === 'error') return <section className="state-card error-state" role="alert"><strong>Local storage is unavailable</strong><p>{message}</p><button type="button" className="secondary-button" onClick={() => { setDataState('loading'); void refreshData(); }}>Try again</button></section>;
    if (activeView === 'home') return <HomeView data={data} onNavigate={navigate} onNew={openNew} />;
    if (activeView === 'calendar') return <CalendarView items={calendarItems} timeFormat={settings.timeFormat} weekStartsOn={settings.weekStartsOn} />;
    if (activeView === 'tasks') return recordsView('task', data.tasks);
    if (activeView === 'notes') return recordsView('note', data.notes);
    if (activeView === 'events') return recordsView('event', data.events);
    if (activeView === 'shopping') return recordsView('shopping', data.shoppingItems);
    if (activeView === 'favorites') return <FavoritesView data={data} isCompleted={isCompleted} timeFormat={settings.timeFormat} {...commonRecordActions} />;
    if (activeView === 'more') return <MoreView onNavigate={navigate} />;
    if (activeView === 'settings') return <div className="settings-page"><SettingsView activeTheme={settings.themeId} categories={data.categories} onAddCategory={() => setCategoryState(null)} onDeleteCategory={(category) => void removeCategory(category)} onEditCategory={setCategoryState} onSelectTheme={(themeId) => setSettings((current) => ({ ...current, themeId }))} onSettingsChange={setSettings} settings={settings} /></div>;
    if (activeView === 'data') return <BackupsView appVersion={APP_VERSION} data={data} onRestore={restorePlanner} settings={settings} />;
    return <ModuleView description="This planner module is ready for a future approved phase." icon={Database} moduleName="Planner" onNavigate={navigate} />;
  };

  return (
    <div className="app-shell" data-active-view={activeView} data-app-variant="sale">
      <a className="skip-link" href="#main-content">Skip to content</a>
      <Sidebar activeView={activeView} onNavigate={navigate} />
      <div className="content-column">
        <TopBar activeTheme={settings.themeId} onOpenQuickAdd={() => setQuickAddOpen(true)} onOpenSearch={() => setSearchOpen(true)} onSelectTheme={(themeId) => setSettings((current) => ({ ...current, themeId }))} pageDescription={activeNavigation.description} pageTitle={activeNavigation.label} />
        <PwaNotices offline={offline} update={pwaUpdate} />
        {message && dataState !== 'error' && <div className="status-banner" role="status"><span>{message}</span><button type="button" aria-label="Dismiss message" onClick={() => setMessage('')}>×</button></div>}
        <main className="page-shell" data-view={activeView} id="main-content" ref={mainRef} tabIndex={-1}>{renderActiveView()}</main>
        <Footer />
      </div>
      <MobileNav activeView={activeView} onNavigate={navigate} />
      <QuickAddDialog open={quickAddOpen} onClose={() => setQuickAddOpen(false)} onChoose={(viewId) => { setQuickAddOpen(false); openNew(viewId === 'tasks' ? 'task' : 'event'); }} />
      <EntryDialog categories={data.categories} kind={entryState?.kind ?? 'task'} onClose={() => setEntryState(null)} onSave={saveEntry} open={entryState !== null} record={entryState?.record ?? null} />
      <CategoryDialog categories={data.categories} category={categoryState ?? null} onClose={() => setCategoryState(undefined)} onSave={saveCategoryEntry} open={categoryState !== undefined} />
      <SearchDialog data={data} onClose={() => setSearchOpen(false)} onOpenResult={(kind, id) => { const records: Record<RecordKind, PlannerRecord[]> = { task: data.tasks, event: data.events, note: data.notes, shopping: data.shoppingItems }; const record = records[kind].find((item) => item.id === id); setSearchOpen(false); navigate(viewForKind[kind]); if (record) openEdit(kind, record); }} open={searchOpen} />
    </div>
  );
};
